import {HubblTask, Palette, SimpleUser} from 'hubbl-shared';
import {memo, useCallback, useState} from 'react';
import styled, {keyframes} from 'styled-components';
import {TextHeading3} from '../index';
import {LoadingAnimation} from '../loading-animation/LoadingAnimation';
import {HubblTaskCreator} from './HubblTaskCreator';
import {HubblTaskItem} from './HubblTaskItem';
import {useI18n} from '../../hooks';

interface HubblTaskManagerProps {
  tasks: HubblTask[];
  attendees: SimpleUser[];
  currentUserId: string;
  isUserCreator: boolean;
  onTaskCreate: (title: string) => void;
  onTaskAssign?: (taskId: string, userId: string) => void;
  onTaskUnassign?: (taskId: string) => void;
  onTaskDelete: (taskId: string) => void;
  isLoading?: boolean;
  isCreationMode?: boolean;
  isUserAttending?: boolean;
}

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const Container = styled.div`
  position: relative;
  animation: ${fadeInUp} 0.6s ease-out;
`;

const TasksList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 16px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 32px 16px;
  margin-bottom: 16px;
  color: ${Palette.almostBlack500};
  background: ${Palette.almostBlackTransparent5};
  border-radius: 12px;
  border: 1px solid ${Palette.almostBlackTransparent10};
`;

export const HubblTaskManager = memo(
  ({
    tasks,
    attendees,
    currentUserId,
    isUserCreator,
    onTaskCreate,
    onTaskAssign,
    onTaskUnassign,
    onTaskDelete,
    isLoading = false,
    isCreationMode = false,
    isUserAttending = true,
  }: HubblTaskManagerProps) => {
    const t = useI18n();
    const handleCreateTask = useCallback(
      (title: string) => {
        onTaskCreate(title);
        // Don't hide the creator - let users keep adding tasks
      },
      [onTaskCreate],
    );

    const handleTaskAssign = useCallback(
      (taskId: string, userId: string) => {
        onTaskAssign?.(taskId, userId);
      },
      [onTaskAssign],
    );

    const handleTaskUnassign = useCallback(
      (taskId: string) => {
        onTaskUnassign?.(taskId);
      },
      [onTaskUnassign],
    );

    const handleTaskDelete = useCallback(
      (taskId: string) => {
        onTaskDelete(taskId);
      },
      [onTaskDelete],
    );

    if (tasks.length === 0 && !isUserCreator) {
      return null; // Don't show empty state for non-creators
    }

    return (
      <Container>
        {isLoading && <LoadingAnimation overwriteIsBusy />}

        {tasks.length === 0 ? (
          <EmptyState>
            <TextHeading3
              text={t.events.noTasksYet}
              color={Palette.almostBlack500}
            />
          </EmptyState>
        ) : (
          <TasksList>
            {tasks.map(task => (
              <HubblTaskItem
                key={task.id}
                task={task}
                attendees={attendees}
                currentUserId={currentUserId}
                isUserCreator={isUserCreator}
                onAssign={isCreationMode ? undefined : handleTaskAssign}
                onUnassign={isCreationMode ? undefined : handleTaskUnassign}
                onDelete={handleTaskDelete}
                isLoading={isLoading}
                isCreationMode={isCreationMode}
                isUserAttending={isUserAttending}
              />
            ))}
          </TasksList>
        )}

        {/* Always show task creator for creators at the bottom */}
        {isUserCreator && (
          <HubblTaskCreator
            onCreate={handleCreateTask}
            onCancel={() => {}} // No cancel needed since it's always visible
            isLoading={isLoading}
          />
        )}
      </Container>
    );
  },
);
